import { TargetInfo } from 'xcipio/cases/targetInfo/target-info-defs';
import { Identity, KeyValuePair } from '../types';

export interface TargetState {
  targets: TargetModel[],
  target: TargetModel | null,
  byId: TargetModelMap
}

export type TargetModelWrapper = {
  display: boolean
  ids: string[]
  synthesized?: boolean
  target: TargetModel
}

export type TargetModelArrayMap = {
  [targetId: string]: TargetModel[]
}

export type TargetModelMap = {
  [targetId: string]: TargetModel
}

export type TargetModel = {
  id: string | null
  name?: string
  caseId?: Identity
  dxAccess?: string
  targetInfoHash?: {
    afType?: string
    targetInfoType?: string
    targetInfos?: TargetInfo[]
    options?: unknown[]
  }[]
  dxOwner?: string
  afs?: TargetModelAccessFunctions
  isRequired?: boolean
  options?: KeyValuePair[]
  actualPrimaryType?: string
  primaryType?: string
  primaryValue?: string
  secondaryType?: string
  secondaryValue?: string
  synthesized?: boolean
  optionValue?: string
  templateUsed?: string
  timeZone?: string
  type?: string
  validAfs?: unknown
}

export type TargetModelValidation = {
  primaryType?: string
  primaryValue?: string
  isRequired?: boolean
}

export type TargetModelAfCCCSettings = {
  afids: string[]
  autowired: boolean
  isValid?: boolean
  options: KeyValuePair[]
}

export type TargetModelAfCCC = {
  afType: string
  afTemplateId?: string
  afSettings: TargetModelAfCCCSettings[]
}

export type TargetModelAccessFunction = {
  bAFAssociation?: boolean
  x3TargetAssociations?: unknown
  contentInterfaces?: unknown
  id?: string
  model?: string
  name: string
  tag?: string
  type?: string
  cspid?: string
  cspName?: string
}

export type TargetModelAccessFunctions = {
  afIds: TargetModelAccessFunction[]
  afGroups: unknown[]
  afCspGroups?: { id: string, name: string }[]
  afAutowireTypes: {
    name?: string
    id?: string
    tag?: string
  }[]
  afAutowireGroups: unknown[]
  afccc: TargetModelAfCCC[]
}

export const ERICSSON_NE_TAB_TEMPORARY_ID = 'ericssonNETTargetInfoTab';

export type EricssonNETargetInfoTargetFormData = {
  TID: string
  AFID: string
  LEMFID: string
  NETOPID: string
  SUBNETOPID: string
  WARRANTID: string
  LEAID: string
  USEONELEMF: boolean,
  SUPP_INFO: string
}

export const ERICSSON_NE_TARGET_KEY = [
  'TID',
  'AFID',
  'LEMFID',
  'NETOPID',
  'SUBNETOPID',
  'WARRANTID',
  'LEAID',
  'USEONELEMF',
];

export type EricssonNETargetInfoGPRSTargetFormData = {
  TID: string
  AFID: string
  GGSNMONITOR: string
  SUPP_LOCINFO: string
}

export type EricssonNETargetInfoMSCTargetFormData = {
  SF_USSD: string
  SF_HOI: string
  SF_NCIS: string
  SF_CRSS: string
  SF_IOBS: string
  SF_DDE: string
  SF_DFA: string
  SF_LCUP: string
  SF_VCE: string
  SF_CISS: string
  SF_UDI: string
  SF_NCRS: string
  SF_AVF: string
  SF_AVMC: string
  SF_F31: string
}

export type EricssonNETargetInfoMonitoringFormData = {
  temporaryId: string
  TID: string
  AFID: string
  MCNBID: string
  MCNB: string
  MCNBTYPE: string
  DESCRIPTION: string
  HI3NAME: string
  HOSTNAME: string
  MCNBNAME: string
  MCNBPORT: string
  CHANNELNUM: string
  SECMASMCNB_ID: string
}

export type EricssonNETargetInfoFIXTargetFormData = {
  TID: string
  AFID: string
  LEGALBASIS: string
  CLOSEUSERGROUP: string
  NTWKID: string
  NUMSUPPRESSED: boolean
  VERIFYMCNB: boolean
  PABXNAME: string
  CTYPE: string
  MAXCALL: string
}

export type EricssonNETargetInfoMCTargetFormData = {
  IRI_MCID: string,
  IRI_SUPPNEID: string,
  CC_MCID: string,
  CC_SUPPNEID: string
}

export const ERICSSON_NE_MC_KEY = [
  'IRI_MCID',
  'IRI_SUPPNEID',
  'CC_MCID',
  'CC_SUPPNEID',
]

export type EricssonNETargetInfoGeneralFormData = {
  targetDeliveryTemplate: string
}

export type EricssonNETargetInfoFormData = {
  EIMSTARGET: EricssonNETargetInfoTargetFormData
  EIMSMSCTRGT?: EricssonNETargetInfoMSCTargetFormData
  EIMSGPRSTRGT?: EricssonNETargetInfoGPRSTargetFormData
  EIMSWRLNTRGT?: EricssonNETargetInfoFIXTargetFormData
  EIMSMC?: EricssonNETargetInfoMCTargetFormData
  EIMSMCNB?: EricssonNETargetInfoMonitoringFormData[]
  general?: EricssonNETargetInfoGeneralFormData
}

export type UtimacoAFTargetInforGeneralFormData = {
  targetDeliveryTemplate: string
}

export type UtimacoAFTargetInforTargetFormData = {
  LIID: string
}

export type UtimacoAFTargetInfoMonitoringFromData = {
  connectedLinePresentation: boolean
  suppressInterceptNumber: boolean
  separateCallContentChannel: boolean
}

export type UtimacoAFTargetInfoICDFormData = {
  LEA: string,
  FILEREF: string,
  CLASS: string
}

export type UtimacoAFTargetInfoNetworkFormData = {
  IA: boolean
  IMS: boolean
  POC: boolean
  NGN: boolean
  VOIP: boolean
}

export type UtimacoAFTargetInfoCallContentFormData = {
  DATA: boolean
  IA: boolean
  MM: boolean
}

export type UtimacoAFTargetInfoMonitorCenterIdData = {
  CCCDATAID?: string
  CCCIAID?: string
  CCCMMID?: string
}

export type UtimacoAFTargetInforFormData = {
  general?: UtimacoAFTargetInforGeneralFormData
  targetInformation?: UtimacoAFTargetInforTargetFormData
  monitoringCenterFlags?: UtimacoAFTargetInfoMonitoringFromData
  ICD?: UtimacoAFTargetInfoICDFormData
  networkType?: UtimacoAFTargetInfoNetworkFormData
  callContentType?: UtimacoAFTargetInfoCallContentFormData
  monitoringCenterNumberInformation?: UtimacoAFTargetInfoMonitorCenterIdData
}
